import React, { useEffect, useState } from "react";
import { getBusinesses, saveBusiness } from "../../components/Api/GmsBusiness";
import { getCompanies } from "../../components/Api/GmsCompany";
import { getCountries } from "../../components/Api/GmsCountry";
import Select from "react-select";
import { Button } from "reactstrap";
import Swal from "sweetalert2";
import { Eye } from "lucide-react";
import { ClipLoader } from "react-spinners";
import 'bootstrap/dist/css/bootstrap.min.css';
import API_URL from '../../config';
import {
  getUserPermissions,
  hasPagePermissionWithAdmin,
  hasEditPermissionWithAdmin,
  hasDeletePermissionWithAdmin
} from "../../components/Permission/PermissionService";
import { validateFile, resizeImage } from "../../utils/fileUploadHelper";

const defaultForm = {
  business_Id: null,
  business_Code: "",
  business_NameEn: "",
  business_NameAr: "",
  business_Company_Id: "",
  business_Active: true,
  business_TRN: "", // New field for TRN
  business_Address: "", // New field for Address
  business_ContactNo: "", // New field for Contact Number
  business_Country_Id: null, // New field for Country
  business_LogoPath: null, // New field for Logo Path
  business_Logo: null, // New field for Logo File
  whatsApp_AccessToken: "",
  whatsApp_PhoneNumberId: "",
  whatsApp_WabaId: "",
  whatsApp_VerifyToken: "",
  whatsApp_ApiVersion: "v18.0",
  business_CreatedDate: new Date().toISOString(),
  business_CreatedBy: Number(localStorage.getItem("userId")) || 1,
  business_ModifiedDate: new Date().toISOString(),
  business_ModifiedBy: Number(localStorage.getItem("userId")) || 1,
  whatsappNumber: "",
  businessReportIntegration: false,
  customerReportIntegration: false,
};

const emptyFilter = {
  business_Code: "",
  business_NameEn: "",
  business_NameAr: "",
  business_Company_Id: null,
  business_Active: null,
};

// Add Logo Preview Component
const LogoPreview = ({ file, existingLogo }) => {
  if (!file && !existingLogo) return null;

  let imageUrl;
  if (file) {
    imageUrl = URL.createObjectURL(file);
  } else if (existingLogo) {
    imageUrl = existingLogo.startsWith('http') ? existingLogo : `${API_URL}${existingLogo}`;
  }

  return (
    <div className="mt-2">
      <img
        src={imageUrl}
        alt="Business Logo"
        style={{
          maxWidth: '200px',
          maxHeight: '200px',
          objectFit: 'cover',
          borderRadius: '8px'
        }}
      />
    </div>
  );
};

export default function BusinessPage() {
  const [businesses, setBusinesses] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [showFilters, setShowFilters] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);

  // Permission states
  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Load permissions first
  useEffect(() => {
    loadPermissions();
  }, []);

  // Load data after permissions are loaded
  useEffect(() => {
    if (!permissionsLoading && canView) {
      loadBusinesses();
      loadCompanies();
      loadCountries();
    }
  }, [permissionsLoading, canView, page]);

  const loadPermissions = async () => {
    try {
      const userPermissions = await getUserPermissions();
      setPermissions(userPermissions);

      // Check permissions for this page
      const hasView = hasPagePermissionWithAdmin(userPermissions, '/GmsBusiness');
      const hasEdit = hasEditPermissionWithAdmin(userPermissions, '/GmsBusiness');
      const hasDelete = hasDeletePermissionWithAdmin(userPermissions, '/GmsBusiness');

      setCanView(hasView);
      setCanEdit(hasEdit);
      setCanDelete(hasDelete);

      // If no view permission, redirect to dashboard
      if (!hasView) {
        Swal.fire("Access Denied", "You don't have permission to view this page.", "error").then(() => {
          window.location.href = '/Dashboard';
        });
      }
    } catch (error) {
      console.error("Error loading permissions:", error);
      Swal.fire("Error", "Failed to load permissions. Redirecting to dashboard.", "error").then(() => {
        window.location.href = '/Dashboard';
      });
    } finally {
      setPermissionsLoading(false);
    }
  };

  const loadCompanies = async () => {
    try {
      const data = await getCompanies({});
      setCompanies(data);
    } catch (error) {
      console.error("Error loading companies:", error);
    }
  };

  const loadCountries = async () => {
    try {
      const data = await getCountries({});
      setCountries(data);
    } catch (error) {
      console.error("Error loading countries:", error);
    }
  };

  const loadBusinesses = async (filterData = emptyFilter) => {
    setLoading(true);
    try {
      const data = await getBusinesses(filterData);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = data.slice(startIndex, endIndex);
      setBusinesses(paginatedData);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load companies: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLogoChange = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Validate file (images only, max 5MB, no video)
      const validation = validateFile(file, { 
        maxSizeMB: 5, 
        allowedTypes: ['image/jpeg', 'image/png'] 
      });

      if (!validation.valid) {
        // Reset file input if validation fails
        e.target.value = "";
        return;
      }

      // Resize image to 512x512
      try {
        const resizedFile = await resizeImage(file, 512, 512);
        handleFormChange("business_Logo", resizedFile);
        console.log('Processed business logo:', {
          name: resizedFile.name,
          type: resizedFile.type,
          size: resizedFile.size
        });
      } catch (error) {
        console.error("Error resizing logo:", error);
        Swal.fire("Error", "Failed to process logo.", "error");
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    handleFormChange(name, type === "checkbox" ? checked : value);
  };

  const getLogoDisplayName = () => {
    if (form.business_Logo) {
      return form.business_Logo.name;
    }
    if (form.business_LogoPath) {
      return form.business_LogoPath.split("/").pop();
    }
    return "No file chosen";
  };

  // Set default active status based on permissions
  useEffect(() => {
    if (!isEditing && canEdit && !canDelete) {
      setForm(prev => ({
        ...prev,
        business_Active: true
      }));
    }
  }, [isEditing, canEdit, canDelete]);

  const handleCompanyChange = (selectedOption) => {
    setForm({
      ...form,
      business_Company_Id: selectedOption ? selectedOption.value : "",
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value === "" ? null : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      console.log("Submitting business", {
        ...form,
        business_Logo: form.business_Logo ? form.business_Logo.name : null
      });

      const formData = new FormData();

      // Handle logo file
      if (form.business_Logo) {
        formData.append('logoFile', form.business_Logo);
        console.log('New logo file being sent:', {
          name: form.business_Logo.name,
          type: form.business_Logo.type,
          size: form.business_Logo.size
        });
      }

      // Add all form fields to FormData
      Object.keys(form).forEach(key => {
        if (form[key] !== null && form[key] !== undefined && key !== 'business_Logo') {
          formData.append(key, form[key]);
        }
      });

      // Update modified date
      formData.append('business_ModifiedDate', new Date().toISOString());

      const result = await saveBusiness(formData);
      Swal.fire("Success", result.message, "success");
      setForm(defaultForm);
      setIsEditing(false);
      loadBusinesses(filter);
    } catch (error) {
      Swal.fire("Error", "Failed to save company: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (business) => {
    setViewLoadingRow(business.business_Id);
    try {
      const companyName = companies.find(c => c.company_Id === business.business_Company_Id)?.company_NameEn || business.business_Company_Id;

      Swal.fire({
        title: business.business_NameEn,
        html: `
          <div class="text-left">
            <div class="mb-3 text-center">
              ${business.business_LogoPath ?
            `<img src="${API_URL}${business.business_LogoPath}" alt="${business.business_NameEn}" style="width: 100px; height: 100px; border-radius: 50%; object-fit: cover; margin-bottom: 10px;">` :
            '<div style="width: 100px; height: 100px; border-radius: 50%; background-color: #e9ecef; display: flex; align-items: center; justify-content: center; margin: 0 auto 10px;"><i class="bx bx-building" style="font-size: 40px; color: #adb5bd;"></i></div>'
          }
            </div>
            <div class="mb-3">
              <h6 class="mb-2">Basic Information</h6>
              <p><strong>Code:</strong> ${business.business_Code}</p>
              <p><strong>Name (English):</strong> ${business.business_NameEn}</p>
              <p><strong>Name (Arabic):</strong> ${business.business_NameAr}</p>
              <p><strong>Group:</strong> ${companyName}</p>
              <p><strong>TRN:</strong> ${business.business_TRN || 'N/A'}</p>
              <p><strong>Address:</strong> ${business.business_Address || 'N/A'}</p>
              <p><strong>Contact Number:</strong> ${business.business_ContactNo || 'N/A'}</p>
              <p><strong>Country:</strong> ${countries.find(c => c.country_Id === business.business_Country_Id)?.country_NameEn || 'N/A'}</p>
              <p><strong>WhatsApp Phone ID:</strong> ${business.whatsApp_PhoneNumberId || 'N/A'}</p>
              <p><strong>WhatsApp WABA ID:</strong> ${business.whatsApp_WabaId || 'N/A'}</p>
              <p><strong>WhatsApp API Version:</strong> ${business.whatsApp_ApiVersion || 'N/A'}</p>
              <p><strong>WhatsApp Number:</strong> ${business.whatsappNumber || 'N/A'}</p>
              <p><strong>Business Report Integration:</strong> ${business.businessReportIntegration ? "Yes" : "No"}</p>
              <p><strong>Customer Report Integration:</strong> ${business.customerReportIntegration ? "Yes" : "No"}</p>
              <p><strong>Status:</strong> ${business.business_Active ? "Active" : "Inactive"}</p>
            </div>
          </div>
        `,
        icon: "info",
        width: '600px',
        customClass: {
          container: 'custom-swal-container',
          popup: 'custom-swal-popup',
          content: 'custom-swal-content'
        }
      });
    } catch (error) {
      Swal.fire("Error", "Failed to view company details", "error");
    } finally {
      setViewLoadingRow(null);
    }
  };

  const handleEdit = (business) => {
    console.log("Editing business", business);
    console.log("Existing logo path", business.business_LogoPath);
    setForm({
      ...business,
      business_ModifiedDate: new Date().toISOString(),
    });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const handleCancelEdit = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadBusinesses(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadBusinesses(emptyFilter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  // Generate options for searchable dropdowns
  const businessCodeOptions = businesses.map(b => ({
    value: b.business_Code,
    label: b.business_Code
  }));

  const businessNameEnOptions = businesses.map(b => ({
    value: b.business_NameEn,
    label: b.business_NameEn
  }));

  const businessNameArOptions = businesses.map(b => ({
    value: b.business_NameAr,
    label: b.business_NameAr
  }));

  const companyIdOptions = [...new Set(businesses.map(b => b.business_Company_Id))].map(id => ({
    value: id,
    label: id?.toString()
  }));

  const handleSelectChange = (selectedOption, field) => {
    setFilter({
      ...filter,
      [field]: selectedOption ? selectedOption.value : null
    });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Show loading spinner while permissions are loading
  if (permissionsLoading) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body text-center py-5">
              <ClipLoader size={50} color="#556ee6" />
              <p className="mt-3">Loading permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no view permission
  if (!canView) {
    return null;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Company Management</h4>
        </div>

        {/* Form Section - Only show if user has edit permission */}
        {canEdit && (
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="card-title mb-0">
                {isEditing ? "Edit Company" : "Company Details"}
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Name (English)</label>
                    <input
                      className="form-control"
                      name="business_NameEn"
                      placeholder="Name in English"
                      value={form.business_NameEn}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Name (Arabic)</label>
                    <input
                      className="form-control"
                      name="business_NameAr"
                      placeholder="Name in Arabic"
                      value={form.business_NameAr}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Group</label>
                    <Select
                      value={companies.find(c => c.company_Id === form.business_Company_Id)
                        ? { value: form.business_Company_Id, label: companies.find(c => c.company_Id === form.business_Company_Id).company_NameEn }
                        : null}
                      onChange={handleCompanyChange}
                      options={companies.map(company => ({
                        value: company.company_Id,
                        label: company.company_NameEn
                      }))}
                      isClearable
                      placeholder="Select Group..."
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">TRN</label>
                    <input
                      className="form-control"
                      name="business_TRN"
                      placeholder="Enter TRN (Tax Registration Number)"
                      value={form.business_TRN}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Address</label>
                    <input
                      className="form-control"
                      name="business_Address"
                      placeholder="Enter Address"
                      value={form.business_Address}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contact Number</label>
                    <input
                      className="form-control"
                      name="business_ContactNo"
                      type="tel"
                      placeholder="Enter Contact Number"
                      value={form.business_ContactNo}
                      onChange={handleInputChange}
                      onKeyPress={(e) => {
                        // Only allow numbers, +, -, (, ), and spaces
                        const char = String.fromCharCode(e.which);
                        if (!/[0-9+\-() ]/.test(char)) {
                          e.preventDefault();
                        }
                      }}
                      pattern="[0-9+\-() ]*"
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">WhatsApp Number (For Updates)</label>
                    <input
                      className="form-control"
                      name="whatsappNumber"
                      type="tel"
                      placeholder="Enter WhatsApp Number"
                      value={form.whatsappNumber || ""}
                      onChange={handleInputChange}
                      onKeyPress={(e) => {
                        // Only allow numbers, +, -, (, ), and spaces
                        const char = String.fromCharCode(e.which);
                        if (!/[0-9+\-() ]/.test(char)) {
                          e.preventDefault();
                        }
                      }}
                      pattern="[0-9+\-() ]*"
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Country</label>
                    <Select
                      value={countries.find(c => c.country_Id === form.business_Country_Id)
                        ? { value: form.business_Country_Id, label: countries.find(c => c.country_Id === form.business_Country_Id).country_NameEn }
                        : null}
                      onChange={(option) => handleFormChange('business_Country_Id', option ? option.value : null)}
                      options={countries.map(c => ({
                        value: c.country_Id,
                        label: c.country_NameEn
                      }))}
                      isClearable
                      placeholder="Select Country..."
                    />
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Company Logo</label>
                      <input
                        key={form.business_Logo ? form.business_Logo.name : 'file-input'}
                        id="businessLogoUpload"
                        type="file"
                        className="d-none"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                      <div className="input-group">
                        <label className="input-group-text mb-0" htmlFor="businessLogoUpload">
                          Choose File
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={getLogoDisplayName()}
                          readOnly
                        />
                      </div>
                      <LogoPreview
                        file={form.business_Logo}
                        existingLogo={form.business_LogoPath}
                      />
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="business_Active"
                        name="business_Active"
                        checked={form.business_Active}
                        onChange={(e) => handleFormChange("business_Active", e.target.checked)}
                        disabled={!canDelete} // Only allow status change if user has delete permission
                      />
                      <label className="form-check-label" htmlFor="business_Active">{form.business_Active ? "Active" : "Inactive"}</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check form-switch mt-4 mt-md-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="businessReportIntegration"
                        name="businessReportIntegration"
                        checked={form.businessReportIntegration || false}
                        onChange={(e) => handleFormChange("businessReportIntegration", e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="businessReportIntegration">Business Report Integration</label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="form-check form-switch mt-4 mt-md-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="customerReportIntegration"
                        name="customerReportIntegration"
                        checked={form.customerReportIntegration || false}
                        onChange={(e) => handleFormChange("customerReportIntegration", e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="customerReportIntegration">Customer Report Integration</label>
                    </div>
                  </div>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-12">
                    <h5 className="font-size-14 bg-light p-2 border-bottom mb-3"><i className="bx bxl-whatsapp me-2" style={{ color: '#25D366' }}></i>WhatsApp API Configuration</h5>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">WhatsApp Access Token</label>
                    <input
                      className="form-control"
                      name="whatsApp_AccessToken"
                      placeholder="Enter Meta Access Token"
                      value={form.whatsApp_AccessToken || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Phone Number ID</label>
                    <input
                      className="form-control"
                      name="whatsApp_PhoneNumberId"
                      placeholder="Enter Phone ID"
                      value={form.whatsApp_PhoneNumberId || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">WABA ID</label>
                    <input
                      className="form-control"
                      name="whatsApp_WabaId"
                      placeholder="Enter WABA ID"
                      value={form.whatsApp_WabaId || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6 mt-3">
                    <label className="form-label">Verify Token (For Webhook)</label>
                    <input
                      className="form-control"
                      name="whatsApp_VerifyToken"
                      placeholder="Enter Verify Token"
                      value={form.whatsApp_VerifyToken || ""}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6 mt-3">
                    <label className="form-label">API Version</label>
                    <Select
                      value={form.whatsApp_ApiVersion ? { value: form.whatsApp_ApiVersion, label: form.whatsApp_ApiVersion } : { value: 'v18.0', label: 'v18.0' }}
                      onChange={(option) => handleFormChange('whatsApp_ApiVersion', option ? option.value : 'v18.0')}
                      options={[
                        { value: 'v18.0', label: 'v18.0' },
                        { value: 'v19.0', label: 'v19.0' },
                        { value: 'v20.0', label: 'v20.0' },
                        { value: 'v21.0', label: 'v21.0' }
                      ]}
                      placeholder="Select API Version"
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-success me-2"
                    type="submit"
                    disabled={loading}
                  >
                    <i className="bx bx-check-circle me-2"></i>
                    {loading ? "Saving..." : (isEditing ? "Update Company" : "Save Company")}
                  </button>
                  {isEditing && (
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={handleCancelEdit}
                    >
                      <i className="bx bx-x-circle me-2"></i>
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Filter Section */}
        <div className="card mb-4">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Companies</h5>
              <button
                className="btn btn-primary d-flex align-items-center"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>
          {showFilters && (
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <label className="form-label">Business Code</label>
                  <Select
                    value={businessCodeOptions.find(option => option.value === filter.business_Code)}
                    onChange={(option) => handleSelectChange(option, 'business_Code')}
                    options={businessCodeOptions}
                    isClearable
                    placeholder="Select Business Code"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Name (English)</label>
                  <Select
                    value={businessNameEnOptions.find(option => option.value === filter.business_NameEn)}
                    onChange={(option) => handleSelectChange(option, 'business_NameEn')}
                    options={businessNameEnOptions}
                    isClearable
                    placeholder="Select Name (English)"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Name (Arabic)</label>
                  <Select
                    value={businessNameArOptions.find(option => option.value === filter.business_NameAr)}
                    onChange={(option) => handleSelectChange(option, 'business_NameAr')}
                    options={businessNameArOptions}
                    isClearable
                    placeholder="Select Name (Arabic)"
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Group ID</label>
                  <Select
                    value={companyIdOptions.find(option => option.value === filter.business_Company_Id)}
                    onChange={(option) => handleSelectChange(option, 'business_Company_Id')}
                    options={companyIdOptions}
                    isClearable
                    placeholder="Select Group ID"
                  />
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-primary me-2"
                  onClick={handleApplyFilter}
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table Section */}
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Company List ({businesses.length})</h5>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Logo</th>
                      <th>Business Code</th>
                      <th>Name EN</th>
                      <th>Name AR</th>
                      <th>Group</th>
                      <th>Active</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center">No companies found</td>
                      </tr>
                    ) : (
                      businesses.map((b, index) => (
                        <tr key={b.business_Id}>
                          <td>{(page - 1) * pageSize + index + 1}</td>
                          <td>
                            {b.business_LogoPath ? (
                              <img
                                src={`${API_URL}${b.business_LogoPath}`}
                                alt={b.business_NameEn}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  objectFit: 'cover'
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: '#e9ecef',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <i className="bx bx-building" />
                              </div>
                            )}
                          </td>
                          <td>{b.business_Code}</td>
                          <td>{b.business_NameEn}</td>
                          <td>{b.business_NameAr}</td>
                          <td>{companies.find(c => c.company_Id === b.business_Company_Id)?.company_NameEn || b.business_Company_Id}</td>
                          <td>
                            <span className={`badge ${b.business_Active ? 'bg-success' : 'bg-danger'}`}>
                              {b.business_Active ? "Yes" : "No"}
                            </span>
                          </td>

                          <td>
                            <div className="d-flex gap-2">
                              {canEdit && (
                                <button
                                  className="btn btn-info btn-sm"
                                  onClick={() => handleEdit(b)}
                                >
                                  <i className="bx bx-edit me-1"></i>
                                  Edit
                                </button>
                              )}
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleView(b)}
                                disabled={viewLoadingRow === b.business_Id}
                              >
                                {viewLoadingRow === b.business_Id ? (
                                  <ClipLoader size={16} color="white" />
                                ) : (
                                  <>
                                    <Eye size={16} color="white" />
                                    <span className="ms-1">View</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3"
              style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
              <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>

                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;

                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}